import { GoogleReviewSyncStateView } from '../api/generated/models/GoogleReviewSyncStateView';

/**
 * The seven SyncStatus rows collapse to five rendered states; "sync failed" is derived
 * (idle + a last error), not a status value. Shared by the bar and the page body so both
 * agree on which state the user is in.
 */
export type SyncBarState =
  | 'never_synced'
  | 'in_progress'
  | 'synced'
  | 'failed'
  | 'needs_reconnect';

export function deriveSyncBarState(sync: GoogleReviewSyncStateView | null | undefined): SyncBarState {
  const status = sync?.syncStatus;

  if (status === GoogleReviewSyncStateView.syncStatus.IN_PROGRESS) return 'in_progress';
  if (status === GoogleReviewSyncStateView.syncStatus.NEEDS_RECONNECT) return 'needs_reconnect';

  // idle: distinguish first-run, a failure, and a completed sync.
  if (sync?.lastError) return 'failed';
  if (sync?.lastSyncAt) return 'synced';
  return 'never_synced';
}

/**
 * The button is pressable only when eligible: not running, not reconnect-blocked, and past
 * any cooldown/backoff. A null nextSyncAvailableAt means "now" (never synced, no active backoff).
 */
export function canTriggerSync(sync: GoogleReviewSyncStateView | null | undefined): boolean {
  const state = deriveSyncBarState(sync);
  if (state === 'in_progress' || state === 'needs_reconnect') return false;

  const next = sync?.nextSyncAvailableAt;
  if (!next) return true;
  return new Date(next).getTime() <= Date.now();
}

/** "21h 12m" / "12m" / "" once the target has passed. Drives the disabled-button countdown. */
export function formatCountdown(nextSyncAvailableAt: string | null | undefined): string {
  if (!nextSyncAvailableAt) return '';

  const diffMs = new Date(nextSyncAvailableAt).getTime() - Date.now();
  if (diffMs <= 0) return '';

  const totalMinutes = Math.ceil(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}
